/**
 * What a lice metric *is*, shared by the three chart builders.
 *
 * Split out of `liceChartSpecs`: the vocabulary — which counts exist, what colour each carries
 * and which wire field it reads — is one concern, and building a chart out of it is another.
 */

import { graphSettings } from '@/config/settings';

/**
 * A day as the lice charts consume it: the counts, plus the sea temperature.
 *
 * The field names are the wire's, deliberately — `domain/lice` aliases the generated types
 * rather than renaming them, so a row goes straight into a builder. The fields the endpoints
 * always send are **required**: when they were all optional, this interface accepted a row that
 * carried none of them and the fish-count chart silently drew nothing, because the builder read
 * a `fishCount` the wire calls `count`. Requiring them makes that a compile error.
 */
export interface LiceChartDay {
    /** `YYYY-MM-DD`. */
    day: string;
    /** Fish the day's — or the week's — averages were measured over. */
    count: number;
    femaleAvg: number;
    movingStageAvg: number;
    /** Absent for Chilean-lice locations, which do not track Caligus. */
    caligusElongatusAvg?: number | null;
    /** Sent on each daily row; only the pages that draw it supply one. */
    temperature?: number | null;
}

/** Which lice series to draw. The pages each show a different subset. */
export type LiceChartMetric = 'female' | 'movingStage' | 'caligus';

/** `graphSettings.graphColors` keys, which do not match the wire's field names. */
export const METRIC_COLORS: Record<LiceChartMetric, string> = {
    female: graphSettings.graphColors.adult_female,
    movingStage: graphSettings.graphColors.mobile,
    caligus: graphSettings.graphColors.caligus,
};

/** Only the numeric fields — `day` is a `keyof LiceChartDay` too, and indexing by it would
 * widen every point's value to `string | number`. */
type LiceMetricField = 'femaleAvg' | 'movingStageAvg' | 'caligusElongatusAvg';

export const METRIC_FIELDS: Record<LiceChartMetric, LiceMetricField> = {
    female: 'femaleAvg',
    movingStage: 'movingStageAvg',
    caligus: 'caligusElongatusAvg',
};
